import * as i0 from '@angular/core';
import { Injectable, Inject, InjectionToken, APP_INITIALIZER, NgModule, Optional, inject, PLATFORM_ID, ErrorHandler } from '@angular/core';
import * as i1$1 from '@ngrx/store';
import { createFeatureSelector, createAction, props, createReducer, on, Store, StoreModule, createSelector } from '@ngrx/store';
import { map, switchMap, expand, reduce, catchError, filter, first, tap, finalize, mergeMap, retryWhen } from 'rxjs/operators';
import * as i1 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i2 from '@angular/common/http';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, timer, throwError } from 'rxjs';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import * as i2$1 from '@ngrx/router-store';
import { StoreRouterConnectingModule, routerReducer, NavigationActionTiming } from '@ngrx/router-store';

const configFeatureKey = 'config';
const selectConfigState = createFeatureSelector(configFeatureKey);

const CONFIG_LOAD = createAction('[CONFIG] LOAD', props());
const CONFIG_SET = createAction('[CONFIG] SET', props());
const CONFIG_ERROR = createAction('[CONFIG] ERROR WHILE LOADING CONFIG', props());

const _configReducer = createReducer({}, on(CONFIG_SET, (state, { config }) => config) //config hart überschreiben
);
const configReducer = (state, action) => _configReducer(state, action);

/**
 * File created by suenlue on 13.02.21.
 * Copyright (c) 2021 by netTrek GmbH & Co. KG
 */
class ConfigEffectsService {
    constructor(actions$, http, document) {
        this.actions$ = actions$;
        this.http = http;
        this.document = document;
        this.setFatal = createEffect(() => this.actions$.pipe(ofType(CONFIG_LOAD), map(value => value.url), switchMap(url => {
            return this.http.get(url).pipe(// lade die oberste Ebene der config
            expand(configJSON => {
                if (configJSON && configJSON.extends) {
                    // vorherige config konnte gelanden werden und hat die Eigenschaft extends -> nächste config laden
                    return this.http.get(configJSON.extends);
                }
                return EMPTY; // es gibt keine weitere condif mehr
            }), 
            // jede geladen Config wird jetzt zusammengefasst in eine gesamtconfig um Überladbarkeit zu ermöglichen
            reduce((acc, crr) => {
                acc = Object.assign(Object.assign({}, acc), crr); // merge bisherige Configs die geladen wurden
                if (!crr.extends && !!acc.extends) { // falls keine weitere Erweiterung geladen werden muss
                    delete acc.extends; //  lösche vorhandene extends um eine schleife zu umgehen
                }
                return acc;
            }, {}), switchMap(config => {
                return [CONFIG_SET({ config })];
            }), catchError(err => {
                // da im App Init noch keine Komponente gebaut werden kann, müssen
                // Fehlermeldungen manuell angelegt werden!
                console.error('Fehler beim laden der Config', err);
                const errDiv = this.document.createElement('div');
                errDiv.classList.add('config-load-error');
                errDiv.innerHTML = `
            <h1>Fehler beim laden der Config</h1>
            <p>${JSON.stringify(err).replace('\\n', '<br>')}</p>
            `;
                this.document.body.appendChild(errDiv);
                return EMPTY;
            }));
        })), { dispatch: true });
    }
}
ConfigEffectsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ConfigEffectsService, deps: [{ token: i1.Actions }, { token: i2.HttpClient }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
ConfigEffectsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ConfigEffectsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ConfigEffectsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Actions }, { type: i2.HttpClient }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

const CONFIG_URL = new InjectionToken('configuration URL');
const loadConfig = (store, url) => {
    return () => {
        store.dispatch(CONFIG_LOAD({ url }));
        return store.select(selectConfigState)
            .pipe(filter(config => config != undefined && Object.keys(config).length > 0), 
        // tap ( v => console.log ( v ) ),
        first())
            .toPromise();
    };
};
class ConfigStoreModule {
    static forRoot(configURL) {
        return {
            ngModule: ConfigStoreModule,
            providers: [
                { provide: CONFIG_URL, useValue: configURL },
                { provide: APP_INITIALIZER, useFactory: loadConfig, deps: [Store, CONFIG_URL], multi: true }
            ]
        };
    }
}
ConfigStoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ConfigStoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ConfigStoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ConfigStoreModule, imports: [HttpClientModule, i1.EffectsFeatureModule, i1$1.StoreFeatureModule], exports: [StoreModule] });
ConfigStoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ConfigStoreModule, providers: [], imports: [[
            HttpClientModule,
            EffectsModule.forFeature([ConfigEffectsService]),
            StoreModule.forFeature(configFeatureKey, configReducer)
        ], StoreModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ConfigStoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HttpClientModule,
                        EffectsModule.forFeature([ConfigEffectsService]),
                        StoreModule.forFeature(configFeatureKey, configReducer)
                    ],
                    providers: [],
                    exports: [
                        StoreModule,
                    ]
                }]
        }] });

const COM_PROGRESS_RESET = createAction('[COMMUNICATION] Progress RESET');
const COM_PROGRESS_INCREMENT = createAction('[COMMUNICATION] Progress INCREMENT', props());
const COM_PROGRESS_DECREMENT = createAction('[COMMUNICATION] Progress DECREMENT', props());

class AppProgressInterceptorService {
    constructor(store) {
        this.store = store;
    }
    intercept(req, next) {
        this.increment(req.url);
        return next.handle(req)
            .pipe(tap(res => {
            this.lastResponse = res;
        }, (err) => {
            this.error = err;
        }), finalize(() => {
            this.decrement(req.url);
        }) /*, // geht nicht da sonst DECREMENT zu oft gefeuert wird
        retry ( 2 )*/);
    }
    increment(url) {
        // console.log ( 'increment', url );
        this.store.dispatch(COM_PROGRESS_INCREMENT({ url }));
    }
    decrement(url) {
        // console.warn( 'decrement', url );
        timer(300)
            .subscribe(() => this.store.dispatch(COM_PROGRESS_DECREMENT({ url })));
    }
}
AppProgressInterceptorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: AppProgressInterceptorService, deps: [{ token: i1$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
AppProgressInterceptorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: AppProgressInterceptorService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: AppProgressInterceptorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Store }]; } });

const communicationFeatureKey = 'communication';
const selectComState = createFeatureSelector(communicationFeatureKey);
const selectCommunicationProgress = createSelector(selectComState, (state) => state === null || state === void 0 ? void 0 : state.progress);

const _comReducer = createReducer({ progress: 0 }, on(COM_PROGRESS_RESET, (state) => (Object.assign(Object.assign({}, state), { progress: 0 }))), 
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
on(COM_PROGRESS_INCREMENT, (state) => (Object.assign(Object.assign({}, state), { progress: state.progress + 1 }))), 
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
on(COM_PROGRESS_DECREMENT, (state) => (Object.assign(Object.assign({}, state), { progress: Math.max(state.progress - 1, 0) }))));
const comReducer = (state, action) => _comReducer(state, action);

class CommunicationStoreModule {
}
CommunicationStoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: CommunicationStoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CommunicationStoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: CommunicationStoreModule, imports: [HttpClientModule, i1$1.StoreFeatureModule], exports: [StoreModule] });
CommunicationStoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: CommunicationStoreModule, providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AppProgressInterceptorService,
            multi: true
        }
    ], imports: [[
            HttpClientModule,
            StoreModule.forFeature(communicationFeatureKey, comReducer),
        ], StoreModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: CommunicationStoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HttpClientModule,
                        StoreModule.forFeature(communicationFeatureKey, comReducer),
                    ],
                    providers: [
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: AppProgressInterceptorService,
                            multi: true
                        }
                    ],
                    exports: [
                        StoreModule,
                    ]
                }]
        }] });

const routerFeatureKey = 'router';
const selectRouterState = createFeatureSelector(routerFeatureKey);
const selectRouter = createSelector(selectRouterState, (state) => state === null || state === void 0 ? void 0 : state.state);
// Note: The getSelectors method (built-in selectors) works with the routerReducer provided by @ngrx/router-store.
// If you use a custom serializer, you'll need to provide your own selectors.
const selectPreviousUrl = createSelector(selectRouter, (router) => router === null || router === void 0 ? void 0 : router.previousUrl);
const selectUrl = createSelector(selectRouter, (router) => router === null || router === void 0 ? void 0 : router.url);
const selectRouteData = createSelector(selectRouter, (router) => router === null || router === void 0 ? void 0 : router.data);
const selectRouteParams = createSelector(selectRouter, (router) => router === null || router === void 0 ? void 0 : router.params);
const selectQueryParams = createSelector(selectRouter, (router) => router === null || router === void 0 ? void 0 : router.queryParams);

class CustomSerializer {
    serialize(routerState) {
        if (routerState.url !== this.tempUrl) {
            this.previousUrl = this.tempUrl;
            this.tempUrl = routerState.url;
        }
        let route = routerState.root;
        while (route.firstChild) {
            route = route.firstChild;
        }
        const { url, root: { queryParams }, } = routerState;
        const { params, data } = route;
        // Only return an object including the URL, params and query params
        // instead of the entire snapshot
        return { previousUrl: this.previousUrl, url, params, data, queryParams };
    }
}

class RouterStoreModule {
}
RouterStoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: RouterStoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RouterStoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: RouterStoreModule, imports: [i1$1.StoreFeatureModule, i2$1.StoreRouterConnectingModule], exports: [StoreModule,
        StoreRouterConnectingModule] });
RouterStoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: RouterStoreModule, providers: [], imports: [[
            StoreModule.forFeature(routerFeatureKey, routerReducer),
            StoreRouterConnectingModule.forRoot({
                serializer: CustomSerializer,
                navigationActionTiming: NavigationActionTiming.PostActivation
            })
        ], StoreModule,
        StoreRouterConnectingModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: RouterStoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        StoreModule.forFeature(routerFeatureKey, routerReducer),
                        StoreRouterConnectingModule.forRoot({
                            serializer: CustomSerializer,
                            navigationActionTiming: NavigationActionTiming.PostActivation
                        })
                    ],
                    providers: [],
                    exports: [
                        StoreModule,
                        StoreRouterConnectingModule
                    ]
                }]
        }] });

const ERROR_SET = createAction('[ERROR] SET', props());
const ERROR_RESET = createAction('[ERROR] RESET');

/**
 * File created by suenlue on 14.02.21.
 * Copyright (c) 2021 by netTrek GmbH & Co. KG
 */
class ShowErrorHandler {
    constructor(store, email) {
        this.store = store;
        this.email = email;
        this.count = 0;
    }
    show(error) {
        var _a, _b;
        const out = `Fehler:= ${(_a = error.title) !== null && _a !== void 0 ? _a : ''} \nBeschreibung:= ${error.description} \nMail:= ${(_b = this.email) !== null && _b !== void 0 ? _b : ''}`;
        if (++this.count > 1) {
            console.error(out);
            return;
        }
        confirm(out); // hier etwas sinnvollers machen! Also auf dem Bildschir eine schöner Darstellung;
        this.close(); // confirm hält die Methode an - daher wartet das close darauf ausgeführt zu werden
    }
    close() {
        this.count = Math.max(0, --this.count);
        this.store.dispatch(ERROR_RESET());
    }
}
const SHOW_ERROR_HANDLER = new InjectionToken('constructed ShowErrorHandler');

/**
 * File created by suenlue on 13.02.21.
 * Copyright (c) 2021 by netTrek GmbH & Co. KG
 */
class ErrorEffectsService {
    constructor(actions$, showErrorHandler) {
        this.actions$ = actions$;
        this.showErrorHandler = showErrorHandler;
        this.setFatal = createEffect(() => this.actions$.pipe(ofType(ERROR_SET), map(value => value.active), tap(errDetails => {
            if (this.showErrorHandler) {
                this.showErrorHandler.show(errDetails);
            }
        })), { dispatch: false });
    }
}
ErrorEffectsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ErrorEffectsService, deps: [{ token: i1.Actions }, { token: SHOW_ERROR_HANDLER, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
ErrorEffectsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ErrorEffectsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ErrorEffectsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Actions }, { type: ShowErrorHandler, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SHOW_ERROR_HANDLER]
                }] }]; } });

const errorInitState = { active: undefined };

const _errorReducer = createReducer(errorInitState, on(ERROR_SET, (state, { active }) => (Object.assign(Object.assign({}, state), { active }))), on(ERROR_RESET, (state) => (Object.assign(Object.assign({}, state), { active: undefined }))));
const errorReducer = (state, action) => _errorReducer(state, action);

const errorFeatureKey = 'error';
const selectErrorState = createFeatureSelector(errorFeatureKey);
const selectErrorActiveState = createSelector(selectErrorState, (state) => state.active);

/**
 * File created by suenlue on 14.02.21.
 * Copyright (c) 2021 by netTrek GmbH & Co. KG
 */
class DispatchErrorHandler {
    constructor(store, platformId) {
        this.store = store;
        this.platformId = platformId;
    }
    get isOfflineBrowser() {
        return isPlatformBrowser(this.platformId) && navigator && !navigator.onLine;
    }
    dispatchErrorActionFor(error) {
        var _a;
        // if error-store ist HttpErrorResponse - ignore because the HttpInterceptor Catch this Errors
        if (!(error instanceof HttpErrorResponse)) {
            if (error instanceof Error) {
                // HttpErrorResponse - ignore because the HttpInterceptor Catch this Errors
                if (error.message.indexOf('HttpErrorResponse') === -1) {
                    this.setCustomActiveError((_a = this.getClientStack(error)) !== null && _a !== void 0 ? _a : 'Keine Stack Infos');
                }
            }
            else {
                this.setCustomActiveError(this.getClientMessage(error));
            }
        }
        console.error(error);
    }
    getClientMessage(error) {
        if (this.isOfflineBrowser)
            return 'Sie sind nicht mit dem Internet Verbunden';
        return error.message ? error.message : error.toString();
    }
    getClientStack(error) {
        if (this.isOfflineBrowser)
            return 'Sie sind nicht mit dem Internet Verbunden';
        return error.stack;
    }
    setCustomActiveError(description, title = 'Ein Fehler ist aufgetreten!') {
        this.store.select(selectErrorActiveState).pipe(first()).subscribe(crrErr => {
            if (!crrErr) {
                this.store.dispatch(ERROR_SET({ active: { title, description } }));
            }
        });
    }
}
const DISPATCH_ERROR_HANDLER = new InjectionToken('constructed DispatchErrorHandler', {
    providedIn: 'root',
    factory: () => new DispatchErrorHandler(inject(Store), inject(PLATFORM_ID))
});

/**
 * File created by suenlue on 14.02.21.
 * Copyright (c) 2021 by netTrek GmbH & Co. KG
 */
class DispatchServerErrorHandler {
    constructor(store) {
        this.store = store;
    }
    dispatchErrorActionFor(error) {
        this.setCustomActiveError(this.getServerMessage(error));
        console.error(error);
    }
    getServerMessage(error) {
        return error.message;
    }
    setCustomActiveError(description, title = 'Ein Lade-Fehler ist aufgetreten!') {
        this.store.select(selectErrorActiveState).pipe(first()).subscribe(crrErr => {
            if (!crrErr) {
                this.store.dispatch(ERROR_SET({ active: { title, description } }));
            }
        });
    }
}
const DISPATCH_SERVER_ERROR_HANDLER = new InjectionToken('constructed DispatchServerErrorHandler', {
    providedIn: 'root',
    factory: () => new DispatchServerErrorHandler(inject(Store)),
});

/**
 * File created by suenlue on 14.02.21.
 * Copyright (c) 2021 by netTrek GmbH & Co. KG
 */
class GlobalErrorHandler {
    /**
     * ENDE Workaround
     */
    constructor(injector) {
        this.injector = injector;
    }
    get dispatchErrorHandler() {
        if (!this._dispatchErrorHandler) {
            this._dispatchErrorHandler = this.injector.get(DISPATCH_ERROR_HANDLER);
        }
        return this._dispatchErrorHandler;
    }
    handleError(error) {
        this.dispatchErrorHandler.dispatchErrorActionFor(error);
    }
}
GlobalErrorHandler.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: GlobalErrorHandler, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
GlobalErrorHandler.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: GlobalErrorHandler, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: GlobalErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

/**
 * File created by suenlue on 13.02.21.
 * Copyright (c) 2021 by netTrek GmbH & Co. KG
 */
const ERROR_FEEDBACK_MAIL = new InjectionToken('mail address that receive errors');
const ERROR_IGNORE_URLS = new InjectionToken('URLs that will be ignore from Error Interceptor');
const ERROR_IGNORE_STATUS_CODES = new InjectionToken('HTTP Status Codes that should be ignored from this ErrorHandling');

const findStringInUrlList = (urlList, url) => url && urlList.map(val => val.replace(/^./, ''))
    .filter(v => url.indexOf(v) !== -1)
    .length > 0;
const genericRetryStrategy = ({ maxRetryAttempts = 3, scalingDuration = 1000, excludedUrls = [], excludedStatusCodes = [] } = {}) => (attempts) => {
    return attempts.pipe(mergeMap((error, i) => {
        var _a;
        const retryAttempt = i + 1;
        // if maximum number of retries have been met
        // or response is a status code we don't wish to retry, throw error
        if (retryAttempt > maxRetryAttempts ||
            excludedStatusCodes.find(e => e === error.status) ||
            findStringInUrlList(excludedUrls, error.url)) {
            return throwError(error);
        }
        console.warn(`Attempt ${retryAttempt}: retrying in ${retryAttempt *
            scalingDuration}ms - ${(_a = error === null || error === void 0 ? void 0 : error.url) !== null && _a !== void 0 ? _a : ''}`);
        // retry after 1s, 2s, etc...
        return timer(retryAttempt * scalingDuration);
    }) /*,
    finalize(() => console.log('We are done!'))*/);
};
class ServerErrorInterceptor {
    constructor(dispatchErrorHandler, excludedURLs, excludedHttpErrorCodes) {
        this.dispatchErrorHandler = dispatchErrorHandler;
        this.excludedURLs = excludedURLs;
        this.excludedHttpErrorCodes = excludedHttpErrorCodes;
        if (!excludedURLs) {
            this.excludedURLs = [];
        }
        if (!excludedHttpErrorCodes) {
            this.excludedHttpErrorCodes = [];
        }
    }
    /**
     * Sollte es zu einem HttpErrorResponse kommen wird geprüft ob es ein unauthorisierter Zugriff war und entsprechend
     * ein Feedback gegeben. Anderenfalls wird geschaut, ob die URL sein eigenen Error Handlindg hat oder ein globale error-store
     * wird geworfen
     * @param request
     * @param next
     */
    intercept(request, next) {
        // console.log ( request.url );
        return next.handle(request)
            .pipe(retryWhen(genericRetryStrategy({
            excludedUrls: this.excludedURLs,
            excludedStatusCodes: this.excludedHttpErrorCodes,
            maxRetryAttempts: 3,
            scalingDuration: 500
        })), catchError((error) => {
            if (this.notExcludedURL(error.url) && this.notExcludedStatusCode(error.status)) {
                this.dispatchErrorHandler.dispatchErrorActionFor(error);
            }
            return throwError(error);
        }));
    }
    notExcludedURL(url) {
        return !findStringInUrlList(this.excludedURLs, url);
    }
    notExcludedStatusCode(statusCode) {
        return this.excludedHttpErrorCodes.indexOf(statusCode) === -1;
    }
}
ServerErrorInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ServerErrorInterceptor, deps: [{ token: DISPATCH_SERVER_ERROR_HANDLER }, { token: ERROR_IGNORE_URLS, optional: true }, { token: ERROR_IGNORE_STATUS_CODES, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
ServerErrorInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ServerErrorInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ServerErrorInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: DispatchServerErrorHandler, decorators: [{
                    type: Inject,
                    args: [DISPATCH_SERVER_ERROR_HANDLER]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ERROR_IGNORE_URLS]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ERROR_IGNORE_STATUS_CODES]
                }] }]; } });

class ErrorModule {
    /**
     *
     * allowOverridingGlobalErrorHandler
     * ist für den Entwickler damit Ihm klar gemacht wird, dass wenn er hier true eingibt,
     * der globale ErrorHandler von dem Modul beschrieben wird - dies kann dann natürlich
     * Auswirkungen haben auf einen ErrorHandler den man generell in seiner App verwendet.
     * Sollte die App also einen eigenen globale ErrorHandler besitzen muss hier false
     * gesetzt werden und der globale ErrorHandler muss in soweit erweitert werden, dass er
     * folgende Injection nutzt:
     * constructor ( @Inject(DISPATCH_ERROR_HANDLER) private dispatchErrorHandler: DispatchErrorHandler )
     * Anschließend muss nur noch darauf geachtet werden, dass ein Fehler weitergeleitet wird hierfür muss
     * in handleError die this.dispatchErrorHandler.dispatchErrorActionFor Methode aufgrufen werden
     * und dabei der Fehler durchgereicht werden!
     * handleError ( error: unknown | Error ): void {
     *   // hier was du sonst noch tun willst
     *   this.dispatchErrorHandler.dispatchErrorActionFor( error );
     * }
     *
     *
     * errorFeedbackMail
     * Dieser Wert kann via @Optional() @Inject(ERROR_FEEDBACK_MAIL) readonly mail?: string
     * injiziert und genutzt werden - wir verwenden das z.B. um Fehlermeldungen über mailTo
     * zu versenden
     *
     *
     * ignoreHttpErrForThisUrls
     * Eine Liste von Urls / Endpoints die bei eventuellen Fehlern ignoriert werden soll dies macht
     * natürlich nur dann sinn, wenn man die Fehler selber verarbeitet.
     *
     *
     * ignoreHttpStatusCodes
     * Eine Liste von Status Codes die ignoriert werden sollen. Wenn z.B. bei 401 kein Fehler abgefangen
     * werden darf, muss dies hier eingetragen werden
     *
     *
     * showErrHandler
     * Hier kann eine Klasse übergeben werden, welche verwendet werden soll um Fehler visuell für den
     * Nutzer zu zeigen. Man kann hier einfach die ShowErrorHandler importieren und nutzen.
     * Diese kümmert sich darum, dass via confirm der Fehler angezeigt wird.
     *
     * Alternativ kann man aber auch eine eigenen Klasse schreiben, die von ShowErrorHandler erbt und
     * übergeben wird. Die Klasse muss die Methoden
     * - show ( error: ErrorCodeDetails )
     * - close ( )
     * überschreibt. z.B.
     *
     * export class AlertError extends ShowErrorHandler {
     *
     * show ( error: ErrorCodeDetails ) {
     *   const out = `Fehler:= ${error.title??''} \nBeschreibung:= ${error.description} \nMail:= ${this.email??''}`;
     *   alert( out );
     * }
     *
     * close () {
     *    console.log ( 'alert panel could not be closed via js but never forget to reset the error' );
     *    this.store.dispatch( ERROR_RESET() );
     * }
     *
     * }
     *
     * Wenn showErrHandler nich definiert wird muss man sich selber um die Darstellung der Fehler kümmern und nutzt hierfür am
     * besten den Redux (ngRX) Selector: selectErrorActiveState
     * */
    static forRoot(config) {
        if (!config || !(config === null || config === void 0 ? void 0 : config.allowOverridingGlobalErrorHandler)) {
            console.warn(`Achte darauf, dass ein Globaler Errorhandler verwendet werden muss!
      und dieser ErrorHandler als Interface implementiert und in handleError dispatchErrorActionFor
      vom DISPATCH_ERROR_HANDLER token nutzt z.B.

      export class GlobalErrorHandler implements ErrorHandler {

        constructor ( @Inject(DISPATCH_ERROR_HANDLER) private dispatchErrorHandler: DispatchErrorHandler ) {
        }

        handleError ( error: unknown | Error ): void {
          // hier was du sonst noch tun willst
          this.dispatchErrorHandler.dispatchErrorActionFor( error );
        }
    }
      `);
        }
        return {
            ngModule: ErrorModule,
            providers: [
                ...((config === null || config === void 0 ? void 0 : config.allowOverridingGlobalErrorHandler) ? [
                    { provide: ErrorHandler, useClass: GlobalErrorHandler }
                ] : []),
                ...((config === null || config === void 0 ? void 0 : config.errorFeedbackMail) ? [
                    { provide: ERROR_FEEDBACK_MAIL, useValue: config === null || config === void 0 ? void 0 : config.errorFeedbackMail }
                ] : [
                    { provide: ERROR_FEEDBACK_MAIL, useValue: undefined }
                ]),
                ...((config === null || config === void 0 ? void 0 : config.ignoreHttpStatusCodes) ? [
                    { provide: ERROR_IGNORE_STATUS_CODES, useValue: config === null || config === void 0 ? void 0 : config.ignoreHttpStatusCodes }
                ] : []),
                ...((config === null || config === void 0 ? void 0 : config.ignoreHttpErrForThisUrls) ? [
                    { provide: ERROR_IGNORE_URLS, useValue: config === null || config === void 0 ? void 0 : config.ignoreHttpErrForThisUrls }
                ] : []),
                ...((config === null || config === void 0 ? void 0 : config.showErrHandler) ? [
                    { provide: SHOW_ERROR_HANDLER, useClass: config === null || config === void 0 ? void 0 : config.showErrHandler, deps: [
                            Store, ERROR_FEEDBACK_MAIL
                        ] }
                ] : []),
                ...((config === null || config === void 0 ? void 0 : config.customErrDispatcherFactory) ? [
                    {
                        provide: DISPATCH_ERROR_HANDLER,
                        useFactory: config === null || config === void 0 ? void 0 : config.customErrDispatcherFactory,
                        deps: [
                            Store, PLATFORM_ID
                        ]
                    }
                ] : []),
                { provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true },
            ]
        };
    }
}
ErrorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ErrorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ErrorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ErrorModule, imports: [HttpClientModule, i1.EffectsFeatureModule, i1$1.StoreFeatureModule], exports: [StoreModule] });
ErrorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ErrorModule, providers: [], imports: [[
            HttpClientModule,
            EffectsModule.forFeature([ErrorEffectsService]),
            StoreModule.forFeature(errorFeatureKey, errorReducer)
        ], StoreModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ErrorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HttpClientModule,
                        EffectsModule.forFeature([ErrorEffectsService]),
                        StoreModule.forFeature(errorFeatureKey, errorReducer)
                    ],
                    providers: [],
                    exports: [
                        StoreModule
                    ]
                }]
        }] });

class ErrorService {
    constructor(store, platformId) {
        this.store = store;
        this.platformId = platformId;
    }
    resetActiveError() {
        this.store.dispatch(ERROR_RESET());
    }
    setActiveError(active) {
        this.store.dispatch(ERROR_SET({ active }));
    }
    setCustomActiveError(title, description = '') {
        this.store.dispatch(ERROR_SET({ active: { title, description } }));
    }
    getClientMessage(error) {
        if (isPlatformBrowser(this.platformId) && !!navigator && !navigator.onLine) {
            return 'Sie sind nicht mit dem Internet Verbunden';
        }
        return error.message ? error.message : error.toString();
    }
    getClientStack(error) {
        if (isPlatformBrowser(this.platformId) && !!navigator && !navigator.onLine) {
            return 'Sie sind nicht mit dem Internet Verbunden';
        }
        return error.stack;
    }
    getServerMessage(error) {
        return error.message;
    }
    getServerStack(error) {
        // handle stack trace
        return 'stack';
    }
}
ErrorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ErrorService, deps: [{ token: i1$1.Store }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
ErrorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ErrorService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.1", ngImport: i0, type: ErrorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Store }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { AppProgressInterceptorService, COM_PROGRESS_DECREMENT, COM_PROGRESS_INCREMENT, COM_PROGRESS_RESET, CONFIG_ERROR, CONFIG_LOAD, CONFIG_SET, CONFIG_URL, CommunicationStoreModule, ConfigEffectsService, ConfigStoreModule, CustomSerializer, DISPATCH_ERROR_HANDLER, DISPATCH_SERVER_ERROR_HANDLER, DispatchErrorHandler, DispatchServerErrorHandler, ERROR_FEEDBACK_MAIL, ERROR_IGNORE_STATUS_CODES, ERROR_IGNORE_URLS, ERROR_RESET, ERROR_SET, ErrorEffectsService, ErrorModule, ErrorService, GlobalErrorHandler, RouterStoreModule, SHOW_ERROR_HANDLER, ServerErrorInterceptor, ShowErrorHandler, comReducer, communicationFeatureKey, configFeatureKey, configReducer, errorFeatureKey, errorInitState, errorReducer, loadConfig, routerFeatureKey, selectComState, selectCommunicationProgress, selectConfigState, selectErrorActiveState, selectErrorState, selectPreviousUrl, selectQueryParams, selectRouteData, selectRouteParams, selectRouter, selectRouterState, selectUrl };
